<template>
  <Loader
    v-if="isLoading"
    style="z-index: 9999; position: fixed;"
  />
  <EditorModal
    v-if="loaded"
    :title="idEvent ? t('events.headers.editEvent') : t('events.headers.createEvent')"
    :toolbar-right-buttons="toolbarHeaderRightButtons"
    :content-header-toolbar-start="secondToolbarLeftButtons"
    @update:fields="handleFields"
  >
    <template
      v-if="isPanelOpen"
      #sidebar
    >
      <Panel
        v-if="isPanelOpen"
        class="panel-sidebar-builder-stats"
      >
        <template #header>
          <div class="panel-header-content">
            <i class="fas fa-cogs mr-2" />
            <h4>{{ t('events.headers.settings') }}</h4>
          </div>
        </template>

        <template #icons>
          <i
            v-tooltip="t('statsEditor.sidebarPanel.close')"
            class="far fa-times icons-header-panel"
            @click="togglePanel"
          />
        </template>

        <div class="configuration-wrapper">
          <div class="field-group pt-2">
            <div>
              <div class="field-group-content">
                <div class="field-wrapper">
                  <label class="block mb-2">
                    {{ t('events.form.event_name') }}
                  </label>
                  <div class="p-fluid flex">
                    <InputText
                      v-model="event.name"
                      class="input-text"
                    />
                  </div>
                  <FieldErrors
                    :key="componentFieldErrorsKey"
                    :errors="formValidationErrors"
                    field="name"
                    :silent="false"
                  />
                </div>
              </div>
            </div>
          </div>
          <div class="field-group pt-2">
            <div>
              <div class="field-group-content">
                <div class="field-wrapper">
                  <label class="block mb-2">
                    {{ t('events.form.event_code_name') }}
                  </label>
                  <div class="p-fluid flex">
                    <InputText
                      v-model="event.code_name"
                      class="input-text"
                    />
                  </div>
                  <FieldErrors
                    :key="componentFieldErrorsKey"
                    :errors="formValidationErrors"
                    field="code_name"
                    :silent="false"
                  />
                  <div class="block mt-2">
                    <small>
                      {{ t('events.form.event_code_name_legend') }}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Panel>
    </template>
    <template #content>
      <div class="surface-ground p-2">
        <div class="information-wrapper">
          <BaseInformation message="events.infos.properties_description" />
        </div>
        <div
          v-if="isEmptyPrimaryKeys"
          class="information-wrapper"
        >
          <BaseInformation
            severity="warn"
            message="events.warnings.primary_keys"
          />
        </div>

        <EventFieldsForm
          :fields="fields"
          :primary-keys="primaryKeys"
          :event-definition="eventDefinition"
          @update:fields="handleFields"
          @update:primary-keys="handlePrimaryKeys"
        />
      </div>
    </template>
  </EditorModal>
</template>

<script lang="ts">
import {
  onBeforeMount, defineComponent, ref, PropType, reactive,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Panel from 'primevue/panel';
import EventFieldsForm from '@/components/events/EventFieldsForm.vue';
import EditorModal from '@/components/modals/EditorModal.vue';
import { eventsTypeEnum, PrimvueMenuModel } from '@/types';
import Tooltip from 'primevue/tooltip';
import { showToastError, showToastSuccess } from '@/helpers';
import Loader from '@/components/layout/Loader.vue';
import { Event, Field } from '@/types/events-types';
import BaseInformation from '@/components/fields/BaseInformation.vue';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import InputText from 'primevue/inputtext';
import { ErrorConfigForm } from '@/types/automated-scenarios';
import useVuelidate from '@vuelidate/core';
import {
  helpers, maxLength, minLength, required,
} from '@vuelidate/validators';
import { NestApiType, nestCall } from '@/composables/nestApi';

export default defineComponent({
  name: 'EventsEditorModal',
  components: {
    FieldErrors,
    BaseInformation,
    Loader,
    EditorModal,
    EventFieldsForm,
    Panel,
    InputText,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    idEvent: {
      type: [Number, null] as PropType<number | null>,
      required: true,
    },

    eventDefinition: {
      type: Object as PropType<any | null>,
      required: true,
    },
  },

  emits: ['close'],

  setup(props, { emit }) {
    const isLoading = ref(false);

    const { t } = useI18n();

    const internalIdEvent = ref(props.idEvent);
    const loaded = ref(false);
    const componentFieldErrorsKey = ref(0);
    const formValidationErrors = ref({});

    const event = reactive<{ name: string; code_name: string }>({ name: '', code_name: '' });

    const fields = ref<Field[]>([{
      id: 1,
      name: '',
      type: '',
      options: '',
      description: '',
      primary: false,
      required: true,
    }]);
    const isEmptyPrimaryKeys = ref(true);
    const primaryKeys = ref<string[]>([]);

    const fetchEventDefinition = async () => {
      if (!internalIdEvent.value) return;

      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          `/events/${internalIdEvent.value}`,
        );
        const { data } = response;

        event.name = data.name;
        event.code_name = data.code_name;

        fields.value = JSON.parse(data.properties)
          .map((field: any, index: number) => {
            let options: string[] | null = null;
            if (field.type === 'list') {
              options = field.options
                .split(',')
                .map((opt: string) => opt.trim().replace(/'/g, ''))
                .join(', ');
            }

            if (field.primary) {
              primaryKeys.value.push(field.name);
            }

            return {
              id: index + 1,
              name: field.name,
              old_name: field.name,
              type: field.type,
              description: field.description,
              primary: field.primary,
              ...(options && { options }),
              required: field.required,
            };
          });
        if (primaryKeys.value.length > 0) isEmptyPrimaryKeys.value = false;
      } catch (error) {
        await showToastError(t('errorMessages.GENERIC_ERROR'));
      } finally {
        loaded.value = true;
      }
    };

    const isPanelOpen = ref(true);
    const togglePanel = () => {
      isPanelOpen.value = !isPanelOpen.value;
    };

    const handleFields = (updatedFields: any[]) => {
      fields.value = updatedFields;
    };
    const handlePrimaryKeys = (primaryKeysData: any[]) => {
      primaryKeys.value = primaryKeysData;
      isEmptyPrimaryKeys.value = primaryKeys.value.length === 0;
    };
    const validateFormData = async (): Promise<ErrorConfigForm> => {
      const eventCodeName = (value: string) => (new RegExp('^[a-zA-Z0-9_.]+$')).test(value);

      const rules = {
        name: {
          required: helpers.withMessage(t('vuelidate.required'), required),
          minLength: helpers.withMessage(() => t('vuelidate.minLength', { min: 3 }), minLength(3)),
          maxLength: helpers.withMessage(() => t('vuelidate.maxLength', { max: 25 }), maxLength(25)),
        },

        code_name: {
          required: helpers.withMessage(t('vuelidate.required'), required),
          minLength: helpers.withMessage(() => t('vuelidate.minLength', { min: 3 }), minLength(3)),
          maxLength: helpers.withMessage(() => t('vuelidate.maxLength', { max: 25 }), maxLength(25)),
          eventCodeName: helpers.withMessage(t('vuelidate.eventCodeName'), eventCodeName),
        },
      };

      const v$ = useVuelidate(rules, event);
      const success = await v$.value.$validate();

      return {
        success,
        validate: v$,
      };
    };
    const validateFieldName = (name: string): string | null => {
      const reservedWords = ['SELECT', 'FROM', 'WHERE', 'TABLE', 'COLUMN'];
      const regex = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
      const maxNameLength = 64;

      if (name.length > maxNameLength) {
        return t('events.errors.too_long_field_name', { max: maxNameLength });
      }

      if (!regex.test(name)) {
        return t('events.errors.invalid_characters');
      }

      if (reservedWords.includes(name.toUpperCase())) {
        return t('events.errors.reserved_word', { field: name });
      }

      return null;
    };
    const isValidData = () => {
      const invalidDeleteField = fields.value.find((field) => field.delete === undefined);
      if (!invalidDeleteField) {
        showToastError(t('events.errors.empty_fields'));
        return false;
      }

      const emptyFieldName = fields.value.find((field) => !field.name && !field.delete);
      if (emptyFieldName) {
        showToastError(t('events.errors.empty_field_name'));
        return false;
      }

      const fieldNames = new Set();
      const hasError = fields.value.some((field) => {
        if (!field.delete) {
          if (field.name) {
            if (fieldNames.has(field.name)) {
              showToastError(t('events.errors.duplicate_field_name'));
              return true;
            }
            fieldNames.add(field.name);
          }

          const errorMessage = validateFieldName(field.name);
          if (errorMessage) {
            showToastError(t(errorMessage));
            return true;
          }
        }
        return false;
      });

      if (hasError) {
        return false;
      }

      const emptyFieldType = fields.value.find((field) => !field.type && !field.delete);
      if (emptyFieldType) {
        showToastError(t('events.errors.empty_field_type'));
        return false;
      }

      if (primaryKeys.value.length === 0) {
        showToastError(t('events.errors.empty_primary_keys'));
        return false;
      }

      return true;
    };
    const saveEventDefinition = async (): Promise<boolean> => {
      isLoading.value = true;
      const validation = await validateFormData();
      if (!validation.success) {
        formValidationErrors.value = validation.validate;
        isLoading.value = false;
        componentFieldErrorsKey.value += 1;
      } else if (isValidData()) {
        const payload: Event = {
          name: event.name,
          code_name: event.code_name,
          type: eventsTypeEnum.EXTERNAL,
          properties: fields.value
            .filter((field) => {
              if (internalIdEvent.value) {
                return true;
              }
              return !(field.delete === true);
            })
            .map((field) => ({
              name: field.name,
              ...(field.old_name !== field.name ? { old_name: field.old_name } : {}),
              type: field.type,
              description: field.description,
              ...(field.options ? {
                options: field.options.split(',')
                  .map((option) => option.trim()),
              } : {}),
              ...(field.delete !== undefined ? { delete: field.delete } : {}),
              required: field.required,
              primary: field.primary,
            })),
        };

        try {
          if (internalIdEvent.value) {
            payload.id_event = Number(internalIdEvent.value);

            const response = await nestCall(
              'patch',
              NestApiType.CLIENT_API,
              '/events',
              {},
              '',
              payload,
            );

            if (response.status === 200) {
              await showToastSuccess(t('successMessages.savedSuccessful'));
              return true;
            }
            await showToastError(t('errorMessages.GENERIC_ERROR'));
            return false;
          }

          const response = await nestCall(
            'post',
            NestApiType.CLIENT_API,
            '/events',
            {},
            '',
            payload,
          );

          if (response.status === 201) {
            internalIdEvent.value = response.data?.data?.[0]?.id_custom_data ?? null;
            await showToastSuccess(t('successMessages.savedSuccessful'));
            return true;
          }
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          return false;
        } catch (error) {
          await showToastError(t('errorMessages.GENERIC_ERROR'));
          return false;
        }
      }

      return false;
    };

    const toolbarHeaderRightButtons: PrimvueMenuModel[] = [
      {
        label: t('statsEditor.headerToolbar.save'),
        class: 'p-button-secondary',
        items: [
          {
            label: t('statsEditor.headerToolbar.saveAndLeave'),
            command: async () => {
              isLoading.value = true;
              try {
                const success = await saveEventDefinition();
                if (success) {
                  emit('close');
                }
              } catch (error) {
                await showToastError(t('errorMessages.GENERIC_ERROR'));
              } finally {
                isLoading.value = false;
              }
            },
          },
          {
            label: t('statsEditor.headerToolbar.save'),
            command: async () => {
              isLoading.value = true;
              try {
                await saveEventDefinition();
              } catch (error) {
                await showToastError(t('errorMessages.GENERIC_ERROR'));
              } finally {
                isLoading.value = false;
              }
            },
          },
          {
            label: t('statsEditor.headerToolbar.leaveWithoutSaving'),
            command: () => {
              emit('close');
            },
          },
        ],
      },
    ];
    const secondToolbarLeftButtons = [
      {
        icon: 'far fa-fw fa-cog',
        label: t('statsEditor.toolbar.label.settings'),
        tooltip: t('statsEditor.toolbar.tooltip.settings'),
        command: togglePanel,
        class: () => (isPanelOpen.value ? 'active' : ''),
      },
    ];

    onBeforeMount(async () => {
      if (internalIdEvent.value) {
        await fetchEventDefinition();
      } else {
        loaded.value = true;
      }
    });

    return {
      t,
      componentFieldErrorsKey,
      formValidationErrors,
      isLoading,
      event,
      fields,
      isEmptyPrimaryKeys,
      primaryKeys,
      isPanelOpen,
      togglePanel,
      handleFields,
      handlePrimaryKeys,
      toolbarHeaderRightButtons,
      secondToolbarLeftButtons,
      loaded,
    };
  },
});
</script>

<style lang="scss" scoped>
.warning-message {
  display: flex;
  align-items: center;
  background-color: #fff3cd;
  color: #856404;
  padding: 10px 15px;
  border: 1px solid #ffeeba;
  border-radius: 4px;
  font-size: 14px;
  margin: 10px 0;
}

.warning-message .warning-icon {
  margin-right: 10px;
  font-size: 18px;
  color: #856404;
}

@media (max-width: 600px) {
  .warning-message {
    font-size: 12px;
  }

  .warning-message .warning-icon {
    font-size: 16px;
  }
}

::v-deep(.p-inputtextarea) {
  font-size: 0.9rem !important;
}

.information-wrapper {
  padding: 0.2rem 0.1rem;
}
</style>
